import React, { useEffect, useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import DiagramService from "../../../../../services/diagrams/DiagramService";
import { DiagramRepetitionsSetsVolumeForEachTrainingAndDate } from "../../../../../types/types";

type Props = {
    workbookId: number;
};

const DiagramRepetitionsSetsVolume: React.FC<Props> = ({ workbookId }) => {
    const [rawData, setRawData] = useState<DiagramRepetitionsSetsVolumeForEachTrainingAndDate[]>([]);
    const [visibleCategories, setVisibleCategories] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(true);
    const [maxWeek, setMaxWeek] = useState<number>(0);
    const [minWeek, setMinWeek] = useState<number>(1);
    const [maxSets, setMaxSets] = useState<number>(0);

    useEffect(() => {
        DiagramService.getVolumeSetsRepsForEachDateInCategory(workbookId)
            .then((response) => {
                const data: DiagramRepetitionsSetsVolumeForEachTrainingAndDate[] = response.data;

                const initialVisible: Record<string, boolean> = {};
                let localMaxWeek = 0;
                let localMinWeek = Infinity;
                let localMaxSet = 0;

                data.forEach(item => {
                    const week = getWeekNumber(item.trainingWeek);
                    localMaxWeek = Math.max(localMaxWeek, week);
                    localMinWeek = Math.min(localMinWeek, week);
                    localMaxSet = Math.max(localMaxSet, item.sets);
                    initialVisible[item.category] = true; // default: visible
                });

                setRawData(data);
                setVisibleCategories(initialVisible);
                setMaxWeek(localMaxWeek);
                setMinWeek(localMinWeek);
                setMaxSets(localMaxSet);
            })
            .catch(error => console.error("Error fetching diagram data:", error))
            .finally(() => setLoading(false));
    }, [workbookId]);

    const getWeekNumber = (dateStr: string): number => {
        const date = new Date(dateStr);
        const start = new Date(date.getFullYear(), 0, 1);
        const diff = (date.getTime() - start.getTime()) / 86400000;
        return Math.ceil((diff + start.getDay() + 1) / 7);
    };

    const chartData = [
        ["Category", "Week", "Sets", "Volume", "Repetitions"],
        ...rawData
            .filter(item => visibleCategories[item.category])
            .map(item => [
                item.category,
                getWeekNumber(item.trainingWeek),
                item.sets,
                item.volume,
                Number(item.repetitions)
            ])
    ];

    const weekPadding = 1;
    const hAxisMin = Math.max(minWeek - weekPadding, 1);
    const hAxisMax = maxWeek + weekPadding;
    const hAxisTicks = Array.from({ length: hAxisMax - hAxisMin + 1 }, (_, i) => hAxisMin + i);

    const toggleCategory = (category: string) => {
        setVisibleCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const options = {
        animation: {
            startup: false,
            duration: 2000
        },
        title: "Volume / Sets / Repetitions by Category & Week",
        titleTextStyle: { color: '#8e7f90' },
        hAxis: {
            title: "Week of year",
            titleTextStyle: { color: '#8e7f90' },
            viewWindow: {
                min: hAxisMin,
                max: hAxisMax,
            },
            textStyle: { color: '#8e7f90' },
            ticks: hAxisTicks,
        },
        vAxis: {
            title: "Sets",
            titleTextStyle: { color: '#8e7f90' },
            viewWindow: {
                min: 0,
                max: maxSets + 2
            },
            textStyle: { color: '#8e7f90' },
        },
        bubble: { textStyle: { fontSize: 11, color: '#800080' } },
        explorer: { actions: ["dragToZoom", "rightClickToReset"], axis: "horizontal" },
        //tooltip: { isHtml: true },
        backgroundColor: 'transparent',
    };

    if (loading) return <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <span className="loader" />
                        </div>;

    return (
        <div style={{
            overflowX: "auto",
            overflowY: "hidden",
            marginBottom: "2rem",
        }}>
            <div style={{ minWidth: `${chartData.length * 18}px` }}>
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: ".5em",
                }}>
                    {Object.keys(visibleCategories).map(category => (
                        <label key={category} style={{ display: "flex", alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={visibleCategories[category]}
                                onChange={() => toggleCategory(category)}
                            />
                            {category}
                        </label>
                    ))}
                </div>

                <Chart
                    chartType="BubbleChart"
                    width="100%"
                    height="500px"
                    data={chartData}
                    options={options}
                />
            </div>
        </div>
    );
};

export default DiagramRepetitionsSetsVolume;
